import React, { useMemo, useRef, useState, useEffect, useCallback } from 'react';
import { auth, db } from '../firebaseConfig';
import { collection, doc, query, where, orderBy, limit, getDoc, getDocs, getDocsFromServer, writeBatch, serverTimestamp } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import '../styles/SubmitTeamSummaryForm.css';
import { Bomb, Shield, Copy, Check, Info, Zap } from 'lucide-react';
import TopBar from '../components/TopBar';


import { FiChevronLeft } from 'react-icons/fi';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useSearchParams } from 'react-router-dom';

import { getCurrentSeason } from '../data/seasons';
import { listSeasons } from '../utils/seasonApi';
import { rateLimiter, formatTimeUntilReset } from '../utils/rateLimiter';
import { sanitizeTeamName, sanitizeMatchType, sanitizeRegion, sanitizeInput } from '../utils/inputSanitizer';
import { addCSRFToken, validateFormCSRF } from '../utils/csrfProtection';
import { setSecureStorage, getSecureStorage, removeSecureStorage } from '../utils/storageEncryption';
import { clearPlayerStatsSeasonCache } from '../utils/playerStatsSessionCache';
import {
  MATCH_TYPE_CATEGORIES,
  findMatchTypeEntry,
  getMatchTypeOptions,
  getMatchTypeRegions,
  getSeasonConfig,
} from '../utils/matchTypeCatalog';


// Default map pools by season year (overrideable via configurations/stats.seasons[year].mapPools)
const DEFAULT_MAP_POOLS = {
  '2025': {
    HP: ['Summit', 'Hacienda', 'Apocalypse', 'Slums', 'Combine'],
    SND: ['Tunisia', 'Firing Range', 'Slums', 'Standoff', 'Meltdown', 'Coastal'],
    CTL: ['Raid', 'Takeoff', 'Crossfire'],
  },
  '2026': {
    HP: ['Summit', 'Hacienda', 'Arsenal', 'Takeoff', 'Combine'],
    SND: ['Tunisia', 'Firing Range', 'Slums', 'Meltdown', 'Coastal'],
    CTL: ['Raid', 'Crossroads', 'Standoff'],
  },
};

const WRITE_SEASON_ID = getCurrentSeason();

const flipMatchResult = (result) => {
  const normalized = String(result || '').trim().toUpperCase();
  if (normalized === 'WIN' || normalized === 'W') return 'Lose';
  if (normalized === 'LOSE' || normalized === 'LOSS' || normalized === 'L') return 'Win';
  return result;
};

const getSeasonYearFromDate = (dateValue, fallbackYear) => {
  const parsed = new Date(dateValue);
  if (!Number.isNaN(parsed.getTime())) {
    return String(parsed.getFullYear());
  }
  return String(fallbackYear || getCurrentSeason());
};

// Helper to get today's date in YYYY-MM-DD format
const getTodayDate = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getScoreLabelsForMode = (mode) => {
  const normalized = String(mode || '').trim().toUpperCase();
  if (normalized === 'SND' || normalized === 'CTL') {
    return {
      forLabel: 'Your Rounds',
      againstLabel: 'Opponent Rounds',
      forPlaceholder: 'Your Rounds',
      againstPlaceholder: 'Opponent Rounds',
    };
  }

  return {
    forLabel: 'Your Score',
    againstLabel: 'Opponent Score',
    forPlaceholder: 'Your Score',
    againstPlaceholder: 'Opponent Score',
  };
};

const deriveResultFromScore = (scoreFor, scoreAgainst) => {
  const parsedFor = Number(scoreFor);
  const parsedAgainst = Number(scoreAgainst);
  if (!Number.isFinite(parsedFor) || !Number.isFinite(parsedAgainst)) return '';
  if (String(scoreFor).trim() === '' || String(scoreAgainst).trim() === '') return '';
  if (parsedFor === parsedAgainst) return '';
  return parsedFor > parsedAgainst ? 'Win' : 'Lose';
};

// localStorage keys
const STORAGE_KEY_LAST_VALUES = 'team_summary_last_values';
const STORAGE_KEY_DRAFT = 'team_summary_draft';

const TeamSummaryForm = () => {
  const [searchParams] = useSearchParams();
const teamIdFromQuery = searchParams.get('teamId');
const modeIdFromQuery = searchParams.get('modeId');
const seasonIdFromQuery = searchParams.get('seasonId');
  const formRef = useRef(null);
  const [userProfile, setUserProfile] = useState(null);
  const [teamId, setTeamId] = useState(null);
const [teamLogo, setTeamLogo] = useState(null);
const [teamName, setTeamName] = useState('');
const [activeSeason, setActiveSeason] = useState(getCurrentSeason()); // season doc id used for paths
const [activeSeasonYear, setActiveSeasonYear] = useState(getCurrentSeason()); // human-readable year for config keys
const [seasons, setSeasons] = useState([]);
const [seasonTeamId, setSeasonTeamId] = useState('');
const [seasonDisplayName, setSeasonDisplayName] = useState('');
const [teamRegion, setTeamRegion] = useState('');
const [matchDate, setMatchDate] = useState('');
  const [region, setRegion] = useState('');
  const [enemyTeam, setEnemyTeam] = useState('');
  const [mode, setMode] = useState('');
  const [selectedMap, setSelectedMap] = useState('');
  const [matchType, setMatchType] = useState('');
  const [matchCategory, setMatchCategory] = useState('PRACTICE');
  const [matchTypeRegion, setMatchTypeRegion] = useState('GLOBAL');
  const [pointsFor, setPointsFor] = useState('');
  const [pointsAgainst, setPointsAgainst] = useState('');
  const [matchResult, setMatchResult] = useState('');
  const [notes, setNotes] = useState('');
const [mapOptions, setMapOptions] = useState([]);
const [matchTypes, setMatchTypes] = useState([]);
const [matchTypeCatalog, setMatchTypeCatalog] = useState([]);
const [vsTeams, setVsTeams] = useState([]);
const [regions, setRegions] = useState([]);
const [mapPools, setMapPools] = useState({ HP: [], SND: [], CTL: [] });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [lastMatch, setLastMatch] = useState(null);
  const [modeIdCache, setModeIdCache] = useState({}); // Cache for ModeID generation
  const [isEdit, setIsEdit] = useState(false);
  const [editSeasonId, setEditSeasonId] = useState(null);
  const [reciprocalEnemyModeId, setReciprocalEnemyModeId] = useState(null);

  const navigate = useNavigate();

  const matchTypeRegionOptions = useMemo(() => {
    const options = getMatchTypeRegions(matchTypeCatalog, matchCategory);
    return [...new Set([matchTypeRegion, ...options, 'GLOBAL'].filter(Boolean))];
  }, [matchTypeCatalog, matchCategory, matchTypeRegion]);

  const matchTypeOptions = useMemo(() => {
    const catalogOptions = getMatchTypeOptions(matchTypeCatalog, {
      category: matchCategory,
      region: matchTypeRegion,
    }).map(item => item.label);
    return [...new Set([matchType, ...catalogOptions, ...(!catalogOptions.length ? matchTypes : [])].filter(Boolean))];
  }, [matchTypeCatalog, matchCategory, matchTypeRegion, matchType, matchTypes]);

  const selectedMatchTypeEntry = useMemo(() => (
    findMatchTypeEntry(matchTypeCatalog, {
      label: matchType,
      category: matchCategory,
      region: matchTypeRegion,
    })
  ), [matchTypeCatalog, matchType, matchCategory, matchTypeRegion]);

  useEffect(() => {
    if (!selectedMatchTypeEntry) return;
    if (selectedMatchTypeEntry.category !== matchCategory) {
      setMatchCategory(selectedMatchTypeEntry.category);
    }
    if ((selectedMatchTypeEntry.region || 'GLOBAL') !== matchTypeRegion) {
      setMatchTypeRegion(selectedMatchTypeEntry.region || 'GLOBAL');
    }
  }, [selectedMatchTypeEntry, matchCategory, matchTypeRegion]);

useEffect(() => {
  const unsub = onAuthStateChanged(auth, async (user) => {
    if (!user) return;

    const userRef = doc(db, `users/${user.uid}`);
    const userSnap = await getDoc(userRef);

    if (userSnap.exists()) {
      const data = userSnap.data();
      setUserProfile(data);

      // Priority: use query teamId if available, else fallback to auth-based
      const finalTeamId = teamIdFromQuery || data.teamId;

      if (finalTeamId) {
        setTeamId(finalTeamId);
        const teamRef = doc(db, `teams/${finalTeamId}`);
        const teamSnap = await getDoc(teamRef);
        if (teamSnap.exists()) {
          setTeamLogo(teamSnap.data().logo);
          setTeamName(teamSnap.data().name);
          if (teamSnap.data().region) {
            setTeamRegion(String(teamSnap.data().region).trim().toUpperCase());
          }
        }

        // Load seasons and set active
        const rows = await listSeasons(finalTeamId);
        setSeasons(rows);

        // Set initial season based on today's date (will be recalculated when matchDate changes)
        const todayYear = new Date().getFullYear().toString();
        const matchedRow = rows.find((r) => String(r.year || r.id) === todayYear);
        const chosenRow = matchedRow || rows[0] || null;
        const chosenId = chosenRow ? String(chosenRow.id) : getCurrentSeason();
        const chosenYear = chosenRow ? String(chosenRow.year || chosenRow.id) : getCurrentSeason();
        setActiveSeason(chosenId);
        setActiveSeasonYear(chosenYear);

        // Season identity
        const seasonRef = doc(db, `teams/${finalTeamId}/seasons/${chosenId}`);
        const seasonSnap = await getDoc(seasonRef);
        if (seasonSnap.exists()) {
          const s = seasonSnap.data();
          setSeasonTeamId(s.externalTeamId || chosenId || finalTeamId);
          setSeasonDisplayName(s.displayName || s.externalTeamId || teamSnap.data().name || finalTeamId);
        } else {
          setSeasonTeamId(finalTeamId);
          setSeasonDisplayName(teamSnap.data().name || finalTeamId);
        }
      }
    }
  });

  return unsub;
}, [teamIdFromQuery]);

// Prefill existing submission when editing
useEffect(() => {
  const loadExisting = async () => {
    if (!teamId || !modeIdFromQuery) return;

    const seasonId = seasonIdFromQuery || activeSeason || null;
    const docPath = seasonId
      ? `teams/${teamId}/seasons/${seasonId}/database/teamStats/modes/${modeIdFromQuery}`
      : `teams/${teamId}/database/teamStats/modes/${modeIdFromQuery}`;

    const snap = await getDoc(doc(db, docPath));
    if (!snap.exists()) {
      toast.error('Could not load existing submission for editing.');
      return;
    }

    const data = snap.data() || {};
    setIsEdit(true);
    setEditSeasonId(seasonId || null);
    setReciprocalEnemyModeId(data.ReciprocalEnemyModeID || null);

    setMatchDate(data.Date || '');
    setRegion(data.EnemyRegion || '');
    setEnemyTeam(data.EnemyTeam || '');
    setMode(data.Mode || '');
    setSelectedMap(data.Map || '');
    setMatchType(data.MatchType || '');
    setMatchCategory(data.MatchCategory || 'PRACTICE');
    setMatchTypeRegion(data.MatchTypeRegion || 'GLOBAL');
    setPointsFor(data.PF?.toString() || '');
    setPointsAgainst(data.PA?.toString() || '');
    setMatchResult(data.WinLose || '');
    setNotes(data.Notes || '');
  };

  loadExisting();
}, [teamId, modeIdFromQuery, seasonIdFromQuery, activeSeason]);

const applyDuplicateMatch = useCallback((sourceMatch, showToast = true) => {
  setMatchDate(sourceMatch.Date || getTodayDate());
  setRegion(sourceMatch.EnemyRegion || '');
  setEnemyTeam(sourceMatch.EnemyTeam || '');
  setMode(sourceMatch.Mode || '');
  setSelectedMap('');
  setMatchType(sourceMatch.MatchType || '');
  setMatchCategory(sourceMatch.MatchCategory || 'PRACTICE');
  setMatchTypeRegion(sourceMatch.MatchTypeRegion || 'GLOBAL');
  setPointsFor('');
  setPointsAgainst('');
  setMatchResult('');
  setNotes('');

  if (showToast) {
    toast.success('Match context loaded for next map');
  }
}, []);

// Quick duplicate last match
const handleDuplicateLastMatch = useCallback(() => {
  if (!lastMatch) {
    toast.warning('No previous match found');
    return;
  }

  applyDuplicateMatch(lastMatch);
}, [lastMatch, applyDuplicateMatch]);

// Clear form
const handleClearForm = useCallback(() => {
  setMatchDate(getTodayDate());
  setRegion('');
  setEnemyTeam('');
  setMode('');
  setSelectedMap('');
  setMatchType('');
  setMatchCategory('PRACTICE');
  setMatchTypeRegion('GLOBAL');
  setPointsFor('');
  setPointsAgainst('');
  setMatchResult('');
  setNotes('');
  if (formRef.current) {
    formRef.current.reset();
  }
  const draftKey = `${STORAGE_KEY_DRAFT}_${teamId}_${activeSeason || 'root'}`;
  if (teamId) removeSecureStorage(draftKey);
  toast.info('Form cleared');
}, [teamId, activeSeason]);

// Keyboard shortcuts
useEffect(() => {
  const handleKeyDown = (e) => {
    const isInputFocused = ['INPUT', 'TEXTAREA', 'SELECT'].includes(document.activeElement?.tagName);
    
    // Ctrl/Cmd + Enter to submit (works everywhere)
    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
      e.preventDefault();
      if (formRef.current && !isSubmitting) {
        formRef.current.requestSubmit();
      }
      return;
    }
    // Ctrl/Cmd + D to duplicate (only if input not focused)
    if ((e.ctrlKey || e.metaKey) && e.key === 'd' && !isInputFocused) {
      e.preventDefault();
      handleDuplicateLastMatch();
      return;
    }
    // Ctrl/Cmd + K to clear (only if input not focused)
    if ((e.ctrlKey || e.metaKey) && e.key === 'k' && !isInputFocused) {
      e.preventDefault();
      handleClearForm();
    }
  };
  
  window.addEventListener('keydown', handleKeyDown);
  return () => window.removeEventListener('keydown', handleKeyDown);
}, [isSubmitting, handleClearForm, handleDuplicateLastMatch]);










  useEffect(() => {
    if (mode && mapPools[mode]) {
      setMapOptions(mapPools[mode]);
    } else {
      setMapOptions([]);
    }
  }, [mode, mapPools, activeSeasonYear]);

  // When season changes, reset dependent selections to avoid mixing season configs
  useEffect(() => {
    setRegion('');
    setEnemyTeam('');
    setMatchType('');
    setMatchCategory('PRACTICE');
    setMatchTypeRegion('GLOBAL');
    setMode('');
    setSelectedMap('');
    setNotes('');
  }, [activeSeason, mapPools]);

  useEffect(() => {
    if (matchType || matchTypeOptions.length !== 1) return;
    setMatchType(matchTypeOptions[0]);
  }, [matchType, matchTypeOptions]);

  useEffect(() => {
    const fetchStatsConfig = async () => {
      if (!teamId) return;
      // pre-fill map pools with defaults in case config doc is missing
      setMapPools(DEFAULT_MAP_POOLS[activeSeasonYear] || { HP: [], SND: [], CTL: [] });
      try {
        const configRef = doc(db, `teams/${teamId}/configurations/stats`);
        const configSnap = await getDoc(configRef);
        if (!configSnap.exists()) return;
        const config = configSnap.data();
        
        // Get current season's config (for adding new data)
        const seasonConfig = getSeasonConfig(config, activeSeasonYear || activeSeason);
        const rawTeams = seasonConfig.vsTeams || [];
        const matchTypes = seasonConfig.matchTypes || [];
        const catalog = seasonConfig.matchTypeCatalog || [];
        const configMapPools = seasonConfig.mapPools || DEFAULT_MAP_POOLS[activeSeasonYear] || { HP: [], SND: [], CTL: [] };

        const normalizedTeams = rawTeams.map(t => ({
          name: t.name.trim().toUpperCase(),
          region: t.region.trim().toUpperCase()
        }));

        const regions = [...new Set(normalizedTeams.map(t => t.region))].sort();

        setVsTeams(normalizedTeams);
        setRegions(regions);
        setMatchTypes(matchTypes);
        setMatchTypeCatalog(catalog);
        setMapPools(configMapPools);
        if (seasonConfig.teamRegion || config.region) {
          setTeamRegion(String(seasonConfig.teamRegion || config.region).trim().toUpperCase());
        }

        // Season identity from config (preferred over season doc)
        if (seasonConfig.teamId || seasonConfig.teamName) {
          setSeasonTeamId((prev) => seasonConfig.teamId || prev || teamId);
          setSeasonDisplayName((prev) => seasonConfig.teamName || prev || teamName);
        }
      } catch (err) {
        console.error('Failed to fetch config:', err);
      }
    };

    fetchStatsConfig();
  }, [teamId, activeSeason, activeSeasonYear, teamName]);

  // When match date or season list changes, derive season automatically from date year
  useEffect(() => {
    if (!matchDate || !seasons.length) return;
    const yearStr = new Date(matchDate).getFullYear().toString();
    const row = seasons.find((s) => String(s.year || s.id) === yearStr);
    if (row) {
      const newId = String(row.id);
      setActiveSeason(newId);
      setActiveSeasonYear(yearStr);
    }
  }, [matchDate, seasons]);

  // Keep a cached year for the active season (for configs keyed by year)
  useEffect(() => {
    const row = seasons.find((s) => String(s.id) === String(activeSeason));
    if (row) {
      setActiveSeasonYear(String(row.year || row.id));
    }
  }, [activeSeason, seasons]);

  // Fetch last match for quick duplicate
  useEffect(() => {
    const fetchLastMatch = async () => {
      if (!teamId) return;
      try {
      const col = collection(
        db,
        activeSeason
          ? `teams/${teamId}/seasons/${activeSeason}/database/teamStats/modes`
          : `teams/${teamId}/database/teamStats/modes`
      );
      const q = query(col, orderBy('Date', 'desc'), limit(1));
        const snap = await getDocs(q);
        if (!snap.empty) {
          const lastDoc = snap.docs[0].data();
          setLastMatch(lastDoc);
        }
      } catch (err) {
        // Index might not exist, silently fail
      }
    };
    fetchLastMatch();
  }, [teamId, activeSeason]);

  // Load last values and draft on mount
  useEffect(() => {
    if (!teamId) return;
    
    // Try to load draft first (using secure storage)
  const draftKey = `${STORAGE_KEY_DRAFT}_${teamId}_${activeSeason || 'root'}`;
    const draftData = getSecureStorage(draftKey, true);
    if (draftData) {
      try {
        setMatchDate(draftData.matchDate || getTodayDate());
        setRegion(draftData.region || '');
        setEnemyTeam(draftData.enemyTeam || '');
        setMode(draftData.mode || '');
        setSelectedMap(draftData.selectedMap || '');
        setMatchType(draftData.matchType || '');
        setMatchCategory(draftData.matchCategory || 'PRACTICE');
        setMatchTypeRegion(draftData.matchTypeRegion || 'GLOBAL');
        setPointsFor(draftData.pointsFor || '');
        setPointsAgainst(draftData.pointsAgainst || '');
        setMatchResult(draftData.matchResult || '');
        setNotes(draftData.notes || '');
        toast.info('Draft loaded');
      } catch (e) {
        console.error('Failed to load draft:', e);
      }
    } else {
      // Load last successful values (using secure storage)
      const lastKey = `${STORAGE_KEY_LAST_VALUES}_${teamId}_${activeSeason || 'root'}`;
      const saved = getSecureStorage(lastKey, true);
      if (saved) {
        try {
          setMatchDate(getTodayDate()); // Always use today's date
          setRegion(saved.region || '');
          setMode(saved.mode || '');
          setMatchType(saved.matchType || '');
          setMatchCategory(saved.matchCategory || 'PRACTICE');
          setMatchTypeRegion(saved.matchTypeRegion || 'GLOBAL');
        } catch (e) {
          console.error('Failed to load last values:', e);
        }
      } else {
        // Default to today's date
        setMatchDate(getTodayDate());
      }
    }
  }, [teamId, activeSeason]);

  // Auto-save draft to localStorage
  useEffect(() => {
    if (!teamId) return;
    const draftData = {
      matchDate,
      region,
      enemyTeam,
      mode,
      selectedMap,
      matchType,
      matchCategory,
      matchTypeRegion,
      pointsFor,
      pointsAgainst,
      matchResult,
      notes
    };
    const draftKey = `${STORAGE_KEY_DRAFT}_${teamId}`;
    setSecureStorage(draftKey, draftData, true);
  }, [teamId, matchDate, region, enemyTeam, mode, selectedMap, matchType, matchCategory, matchTypeRegion, pointsFor, pointsAgainst, matchResult, notes]);


  const pad3 = (n) => String(n).padStart(3, '0');

// Optimized ModeID generation with caching and faster query
const generateNextModeID = useCallback(async (mode, seasonIdOverride = null) => {
  const slug = mode.toLowerCase();
  const prefix = `mode_${slug}_`;
  const seasonId = seasonIdOverride || activeSeason || WRITE_SEASON_ID;
  
  // Check cache first (valid for 5 seconds to prevent conflicts)
  const cacheKey = `${teamId}_${mode}`;
  const cached = modeIdCache[cacheKey];
  if (cached && Date.now() - cached.timestamp < 5000) {
    return cached.nextId;
  }

  const col = collection(
    db,
    seasonId
      ? `teams/${teamId}/seasons/${seasonId}/database/teamStats/modes`
      : `teams/${teamId}/database/teamStats/modes`
  );
  
  // Faster: Use orderBy with limit instead of full query
  let maxNum = 0;
  try {
    // Try to get the latest ModeID for this mode using orderBy
    const q = query(
      col,
      where('Mode', '==', mode.toUpperCase()),
      orderBy('ModeID', 'desc'),
      limit(1)
    );
    const snap = await getDocs(q);
    
    if (!snap.empty) {
      const latest = snap.docs[0];
      const modeId = latest.id.startsWith(prefix) ? latest.id : (latest.data().ModeID || '');
      if (modeId.startsWith(prefix)) {
        const num = parseInt(modeId.slice(prefix.length), 10);
        if (!Number.isNaN(num)) maxNum = num;
      }
    }
  } catch (err) {
    // Fallback to full query if index doesn't exist
    const q = query(
      col,
      where('ModeID', '>=', `${prefix}0`),
      where('ModeID', '<=', `${prefix}\uf8ff`)
    );
    const snap = await getDocsFromServer(q);
    snap.forEach((d) => {
      const candidates = [d.id, String(d.get?.('ModeID') || '')];
      for (const s of candidates) {
        if (s.startsWith(prefix)) {
          const n = parseInt(s.slice(prefix.length), 10);
          if (!Number.isNaN(n) && n > maxNum) maxNum = n;
        }
      }
    });
  }

  // Generate next ID and verify it doesn't exist
  let next = maxNum + 1;
  let candidate = `${prefix}${pad3(next)}`;
  let ref = doc(
    db,
    seasonId
      ? `teams/${teamId}/seasons/${seasonId}/database/teamStats/modes/${candidate}`
      : `teams/${teamId}/database/teamStats/modes/${candidate}`
  );
  
  // Double-check the doc doesn't exist (race condition protection)
  if ((await getDoc(ref)).exists()) {
    next += 1;
    candidate = `${prefix}${pad3(next)}`;
    ref = doc(
      db,
      seasonId
        ? `teams/${teamId}/seasons/${seasonId}/database/teamStats/modes/${candidate}`
        : `teams/${teamId}/database/teamStats/modes/${candidate}`
    );
  }
  
  // Cache the result
  setModeIdCache(prev => ({
    ...prev,
    [cacheKey]: { nextId: candidate, timestamp: Date.now() }
  }));
  
  return candidate;
}, [teamId, modeIdCache, activeSeason]);

 const handleSubmit = async (e) => {
  e.preventDefault();
  if (isSubmitting) return;
  
  if (!isEdit) {
    // Check rate limit only for new submissions
    const check = rateLimiter.checkLimit('SUBMIT_STATS');
    if (!check.allowed) {
      const timeUntilReset = formatTimeUntilReset(rateLimiter.getTimeUntilReset('SUBMIT_STATS'));
      toast.error(`Rate limit exceeded. Please try again in ${timeUntilReset}.`);
      return;
    }

    if (!rateLimiter.record('SUBMIT_STATS')) {
      const timeUntilReset = formatTimeUntilReset(rateLimiter.getTimeUntilReset('SUBMIT_STATS'));
      toast.error(`Rate limit exceeded. Please try again in ${timeUntilReset}.`);
      return;
    }
  }

  setIsSubmitting(true);

  const form = formRef.current;
  const formData = new FormData(form);
  const data = {
    ...Object.fromEntries(formData.entries()),
    OffWins: formData.get("OffWins"),
    OffLosses: formData.get("OffLosses"),
    DefWins: formData.get("DefWins"),
    DefLosses: formData.get("DefLosses"),
  };

  // Set values from component state
  data.Date = matchDate;
  data.Mode = mode;
  
  // Sanitize text inputs
  data.MatchType = sanitizeMatchType(matchType);
  data.MatchTypeId = selectedMatchTypeEntry?.id || '';
  data.MatchCategory = selectedMatchTypeEntry?.category || matchCategory || '';
  data.MatchTypeRegion = selectedMatchTypeEntry?.region || matchTypeRegion || '';
  data.EnemyRegion = sanitizeRegion(region);
  data.EnemyTeam = sanitizeTeamName(enemyTeam);
  data.Map = sanitizeInput(selectedMap);
  
  data.PF = pointsFor;
  data.PA = pointsAgainst;
  data.WinLose = matchResult;

  // Convert to numbers
  data.PF = parseInt(data.PF || '0', 10);
  data.PA = parseInt(data.PA || '0', 10);
  data.OffWins = parseInt(data.OffWins || '0', 10);
  data.OffLosses = parseInt(data.OffLosses || '0', 10);
  data.DefWins = parseInt(data.DefWins || '0', 10);
  data.DefLosses = parseInt(data.DefLosses || '0', 10);

  // Add CSRF protection
  const payload = addCSRFToken(data);

  try {
    // Validate CSRF token before saving
    if (!validateFormCSRF(payload)) {
      toast.error('Security validation failed. Please refresh and try again.');
      setIsSubmitting(false);
      return;
    }
    
    const targetSeason = isEdit
      ? (seasonIdFromQuery || editSeasonId || activeSeason || WRITE_SEASON_ID)
      : (activeSeason || WRITE_SEASON_ID);
    const normalizedSeasonId = String(targetSeason || activeSeasonYear || getCurrentSeason());
    const reciprocalSeasonId = getSeasonYearFromDate(data.Date || matchDate, activeSeasonYear || getCurrentSeason());
    const modeId = isEdit && modeIdFromQuery
      ? modeIdFromQuery
      : await generateNextModeID(data.Mode, targetSeason);
    const modeRef = doc(
      db,
      targetSeason
        ? `teams/${teamId}/seasons/${targetSeason}/database/teamStats/modes/${modeId}`
        : `teams/${teamId}/database/teamStats/modes/${modeId}`
    );
    // Remove CSRF token before saving to Firestore
    const { _csrf, ...dataToSave } = payload;
    const reciprocalId = isEdit
      ? (reciprocalEnemyModeId || `recip_${modeId}`)
      : `recip_${modeId}`;
    const reciprocalRef = doc(
      db,
      `teams/${teamId}/seasons/${reciprocalSeasonId}/database/enemyStats/modes/${reciprocalId}`
    );
    const reciprocalSnap = await getDoc(reciprocalRef);
    const ownTeamName = sanitizeTeamName(seasonDisplayName || teamName || teamId || '');
    const ownTeamRegion = sanitizeRegion(teamRegion || userProfile?.region || '');

    const writePayload = {
      ...dataToSave,
      ModeID: modeId,
      SeasonId: normalizedSeasonId,
      SeasonTeamId: seasonTeamId || teamId,
      SeasonTeamName: seasonDisplayName || teamName,
      ReciprocalEnemyModeID: reciprocalId,
      ReciprocalEnemySeasonID: reciprocalSeasonId,
    };
    const reciprocalPayload = {
      Date: dataToSave.Date,
      Mode: dataToSave.Mode,
      MatchType: dataToSave.MatchType,
      MatchTypeId: dataToSave.MatchTypeId || '',
      MatchCategory: dataToSave.MatchCategory || '',
      MatchTypeRegion: dataToSave.MatchTypeRegion || '',
      Map: dataToSave.Map,
      Team: dataToSave.EnemyTeam,
      TeamRegion: dataToSave.EnemyRegion,
      EnemyTeam: ownTeamName,
      EnemyRegion: ownTeamRegion,
      PF: Number(dataToSave.PA) || 0,
      PA: Number(dataToSave.PF) || 0,
      WinLose: flipMatchResult(dataToSave.WinLose),
      Notes: dataToSave.Notes || '',
      SeasonId: reciprocalSeasonId,
      SeasonTeamId: seasonTeamId || teamId,
      SeasonTeamName: seasonDisplayName || teamName,
      ModeID: reciprocalId,
      recordStatus: 'STANDALONE',
      source: 'teamSummaryReciprocal',
      sourceCollection: 'teamStats',
      sourceModeId: modeId,
      sourceSeasonId: normalizedSeasonId,
      updatedAt: serverTimestamp(),
    };
    if (!reciprocalSnap.exists()) {
      reciprocalPayload.createdAt = serverTimestamp();
    }

    const batch = writeBatch(db);
    if (isEdit) {
      batch.update(modeRef, writePayload);
    } else {
      batch.set(modeRef, writePayload);
    }
    batch.set(reciprocalRef, reciprocalPayload, { merge: true });
    await batch.commit();
    clearPlayerStatsSeasonCache(teamId, targetSeason);
    clearPlayerStatsSeasonCache(teamId, reciprocalSeasonId);

    setReciprocalEnemyModeId(reciprocalId);
    setLastMatch(writePayload);
    const savedSummary = [ownTeamName, dataToSave.EnemyTeam].filter(Boolean).join(' vs ');
    const savedDetails = [dataToSave.Mode, dataToSave.Map].filter(Boolean).join(' | ');
    toast.success(
      <div className="team-summary-save-toast">
        <strong>{isEdit ? 'Match updated' : 'Match saved'}</strong>
        {(savedSummary || savedDetails) && (
          <span>{[savedSummary, savedDetails].filter(Boolean).join(' | ')}</span>
        )}
        <button type="button" onClick={() => applyDuplicateMatch(writePayload)}>
          Duplicate for Next Map
        </button>
      </div>
    );

    // Save successful values for next time
      const lastKey = `${STORAGE_KEY_LAST_VALUES}_${teamId}_${activeSeason || 'root'}`;
    localStorage.setItem(lastKey, JSON.stringify({
      region,
      mode,
      matchType,
      matchCategory,
      matchTypeRegion
    }));

    // Clear draft
    const draftKey = `${STORAGE_KEY_DRAFT}_${teamId}_${activeSeason || 'root'}`;
    localStorage.removeItem(draftKey);

    // Clear all form fields
    setMatchDate(getTodayDate());
    setRegion('');
    setEnemyTeam('');
    setMode('');
    setSelectedMap('');
    setMatchType('');
    setMatchCategory('PRACTICE');
    setMatchTypeRegion('GLOBAL');
    setPointsFor('');
    setPointsAgainst('');
    setMatchResult('');
    setNotes('');
    if (!isEdit) {
      setReciprocalEnemyModeId(null);
    }
    if (formRef.current) {
      formRef.current.reset();
    }
  } catch (err) {
    console.error("Error saving to Firestore:", err);
    toast.error("Submission failed.");
  } finally {
    setIsSubmitting(false);
  }
};

const scoreLabels = getScoreLabelsForMode(mode);
const handlePointsForChange = (event) => {
  const nextPointsFor = event.target.value;
  setPointsFor(nextPointsFor);
  const derivedResult = deriveResultFromScore(nextPointsFor, pointsAgainst);
  if (derivedResult) setMatchResult(derivedResult);
};

const handlePointsAgainstChange = (event) => {
  const nextPointsAgainst = event.target.value;
  setPointsAgainst(nextPointsAgainst);
  const derivedResult = deriveResultFromScore(pointsFor, nextPointsAgainst);
  if (derivedResult) setMatchResult(derivedResult);
};

const submitSummaryPrimary = [
  seasonDisplayName || teamName || teamId,
  enemyTeam,
].filter(Boolean).join(' vs ');
const submitSummaryDetails = [mode, selectedMap].filter(Boolean).join(' | ');
const submitSummaryContext = [matchCategory, matchTypeRegion, matchType].filter(Boolean).join(' | ');


  return (
    <>
      <TopBar
        userProfile={userProfile}
        teamLogo={teamLogo}
        setUserProfile={setUserProfile}
        setTeamLogo={setTeamLogo}
      />

      <main className="team-summary-page">
        <div className="team-summary-shell">
          <button
            type="button"
            className="stats-back-button"
            onClick={() => {
              sessionStorage.setItem('returnToTab', 'statshub');
              navigate('/my-stats');
            }}
          >
            <FiChevronLeft size={18} />
            Back to Stats Hub
          </button>

          <form ref={formRef} onSubmit={handleSubmit} className="add-stats-form team-summary-form">
            <header className="team-summary-header">
              <div className="team-summary-title-row">
                <div>
                  <p className="team-summary-eyebrow">Submit Team Stats</p>
                  <h2 className="section-header">Submit Team Stats</h2>
                  <p className="team-summary-subtitle">
                    Record a team match result and update your season stats.
                  </p>
                </div>

                {seasonDisplayName && (
                  <div className="team-summary-status">
                    Submitting as {seasonDisplayName} ({seasonTeamId || teamId}) - season auto-selected by match date
                  </div>
                )}
              </div>

              <div className="team-summary-notice">
                <Info size={14} aria-hidden="true" />
                <p>
                  Match options are managed in{' '}
                  <button
                    type="button"
                    onClick={() => {
                      navigate('/settings?tab=database-config');
                    }}
                  >
                    Database Configurations
                  </button>
                  . Opponent records sync automatically for this season.
                </p>
              </div>

              <div className="team-summary-toolbar">
                <div className="team-summary-actions">
                  {lastMatch && (
                    <button
                      type="button"
                      className="summary-tool-button summary-tool-button-accent"
                      onClick={handleDuplicateLastMatch}
                    >
                      <Copy size={14} />
                      Duplicate Last Match <span>Ctrl+D</span>
                    </button>
                  )}

                  <button
                    type="button"
                    className="summary-tool-button"
                    onClick={handleClearForm}
                  >
                    <Zap size={14} />
                    Clear Form <span>Ctrl+K</span>
                  </button>
                </div>

                <div className="summary-autosave">
                  <Check size={13} />
                  <span>Draft saved</span>
                  <kbd>Ctrl+Enter</kbd>
                </div>
              </div>
            </header>

            <section className="form-card match-info-card">
              <h3>Match Details</h3>
              <div className="field-grid field-grid-two">
                <label className="summary-field">
                  <span>Date</span>
                  <input type="date" value={matchDate} onChange={e => setMatchDate(e.target.value)} required />
                </label>

                <label className="summary-field">
                  <span>Team</span>
                  <input
                    name="SeasonTeam"
                    value={seasonDisplayName || seasonTeamId || teamName || teamId || ''}
                    readOnly
                    placeholder="Your Team (season auto-selected by date)"
                  />
                </label>

                {/* Hidden identifiers to keep data consistent */}
                <input type="hidden" name="TeamId" value={teamId || ''} />
                <input type="hidden" name="SeasonId" value={activeSeason || ''} />
                <input type="hidden" name="SeasonTeamId" value={seasonTeamId || ''} />
                <input type="hidden" name="SeasonTeamName" value={seasonDisplayName || ''} />

                <label className="summary-field">
                  <span>Opponent Region</span>
                  <select required value={region} onChange={e => { setRegion(e.target.value); setEnemyTeam(""); }}>
                    <option value="">Select Opponent Region</option>
                    {regions.map((reg, i) => <option key={i} value={reg}>{reg}</option>)}
                  </select>
                </label>

                <label className="summary-field">
                  <span>Opponent Team</span>
                  <select required value={enemyTeam} onChange={e => setEnemyTeam(e.target.value)} disabled={!region}>
                    <option value="">Select Opponent Team</option>
                    {vsTeams.filter(t => t.region === region).map((team, i) => (
                      <option key={i} value={team.name}>{team.name}</option>
                    ))}
                  </select>
                </label>

                <div className="summary-field-group-label">
                  <span>Match Context</span>
                  <small>Classify this match for filtering and statistics.</small>
                </div>

                <label className="summary-field">
                  <span>Match Category</span>
                  <select
                    required
                    value={matchCategory}
                    onChange={e => {
                      setMatchCategory(e.target.value);
                      setMatchType('');
                    }}
                  >
                    {MATCH_TYPE_CATEGORIES.map(option => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </label>

                <label className="summary-field">
                  <span>Competition Region</span>
                  <select
                    required
                    value={matchTypeRegion}
                    onChange={e => {
                      setMatchTypeRegion(e.target.value);
                      setMatchType('');
                    }}
                  >
                    {matchTypeRegionOptions.map(value => (
                      <option key={value} value={value}>{value}</option>
                    ))}
                  </select>
                </label>

                <label className="summary-field summary-field-wide">
                  <span>Competition</span>
                  <select
                    required
                    value={matchType}
                    onChange={e => {
                      const nextMatchType = e.target.value;
                      const entry = findMatchTypeEntry(matchTypeCatalog, {
                        label: nextMatchType,
                        category: matchCategory,
                        region: matchTypeRegion,
                      });
                      setMatchType(nextMatchType);
                      if (entry) {
                        setMatchCategory(entry.category);
                        setMatchTypeRegion(entry.region || 'GLOBAL');
                      }
                    }}
                  >
                    <option value="">Select Competition</option>
                    {matchTypeOptions.map((type, i) => <option key={`${type}-${i}`} value={type}>{type}</option>)}
                  </select>
                </label>
              </div>
            </section>

            <section className="form-card mode-map-card">
              <h3>Mode & Map</h3>
              <div className="field-grid field-grid-two">
                <label className="summary-field">
                  <span>Mode</span>
                  <select required value={mode} onChange={e => setMode(e.target.value)}>
                    <option value="">Select Mode</option>
                    <option value="HP">Hardpoint</option>
                    <option value="SND">Search & Destroy</option>
                    <option value="CTL">Control</option>
                  </select>
                </label>

                <label className="summary-field">
                  <span>Map</span>
                  <select required value={selectedMap} onChange={e => setSelectedMap(e.target.value)}>
                    <option value="">Select Map</option>
                    {mapOptions.map(map => <option key={map} value={map}>{map}</option>)}
                  </select>
                </label>

                <label className="summary-field summary-field-wide">
                  <span>Match Result</span>
                  <select required value={matchResult} onChange={e => setMatchResult(e.target.value)}>
                    <option value="">Match Result</option>
                    <option value="Win">Win</option>
                    <option value="Lose">Lose</option>
                  </select>
                </label>
              </div>
            </section>

            <section className="form-card points-card">
              <h3>Score</h3>
              <div className="field-grid field-grid-two">
                <label className="summary-field">
                  <span>{scoreLabels.forLabel}</span>
                  <input
                    name="PF"
                    type="number"
                    placeholder={scoreLabels.forPlaceholder}
                    value={pointsFor}
                    onChange={handlePointsForChange}
                  />
                </label>

                <label className="summary-field">
                  <span>{scoreLabels.againstLabel}</span>
                  <input
                    name="PA"
                    type="number"
                    placeholder={scoreLabels.againstPlaceholder}
                    value={pointsAgainst}
                    onChange={handlePointsAgainstChange}
                  />
                </label>

                {mode !== "HP" && (
                  <>
                    <label className="summary-field input-icon-wrapper">
                      <span>Offensive Wins</span>
                      <Bomb className="input-icon yellow-icon" size={16} />
                      <input name="OffWins" type="number" placeholder="Offensive Wins" />
                    </label>

                    <label className="summary-field input-icon-wrapper">
                      <span>Offensive Losses</span>
                      <Bomb className="input-icon yellow-icon" size={16} />
                      <input name="OffLosses" type="number" placeholder="Offensive Losses" />
                    </label>

                    <label className="summary-field input-icon-wrapper">
                      <span>Defensive Wins</span>
                      <Shield className="input-icon blue-icon" size={16} />
                      <input name="DefWins" type="number" placeholder="Defensive Wins" />
                    </label>

                    <label className="summary-field input-icon-wrapper">
                      <span>Defensive Losses</span>
                      <Shield className="input-icon blue-icon" size={16} />
                      <input name="DefLosses" type="number" placeholder="Defensive Losses" />
                    </label>
                  </>
                )}
              </div>
            </section>

            <section className="form-card notes-card">
              <h3>Notes</h3>
              <label className="summary-field">
                <span>Match Notes</span>
                <textarea
                  name="Notes"
                  placeholder="Additional notes..."
                  rows={3}
                  value={notes}
                  onChange={e => setNotes(e.target.value)}
                />
              </label>
            </section>

            <div className="team-summary-submit-row">
              <div className="team-summary-submit-summary">
                {(submitSummaryPrimary || submitSummaryDetails) && (
                  <strong>{[submitSummaryPrimary, submitSummaryDetails].filter(Boolean).join(' | ')}</strong>
                )}
                {submitSummaryContext && <span>{submitSummaryContext}</span>}
              </div>
              <span>Ctrl+Enter</span>
              <button type="submit" disabled={!teamId || isSubmitting}>
                {isSubmitting ? 'Inserting...' : <>Submit Match <span aria-hidden="true">-&gt;</span></>}
              </button>
            </div>
          </form>
        </div>
      </main>

      <ToastContainer position="bottom-right" autoClose={3000} />
    </>
  );
};

export default TeamSummaryForm;

